import {
  TrendingDown,
  TrendingUp,
  ArrowRight,
  Zap,
  CheckCircle2,
} from 'lucide-react'
import type { ReactNode } from 'react'
import type { ToolKey, ToolState } from '@/types/radar'
import { TOOLS } from '@/types/radar'
import { usableScore } from '@/lib/radar/tool-scores'
import { buildVerdict } from '@/lib/radar/verdict'
import { InstrumentLabel, Panel } from './ui'

interface ScoreInterpretationProps {
  unifiedScore: number
  unifiedGrade: string
  doneCount: number
  errorCount: number
  warningCount: number
  /**
   * The previous completed audit for the SAME domain, excluding the run on
   * screen. All four `previous*` values come from that one run, or are null
   * together when there is none — see lib/radar/run-history.ts. Null means the
   * movement UI is hidden, never rendered as +-0.
   */
  previousScore?: number | null
  previousIssueCount?: number | null
  previousScanDate?: number | null
  previousToolScores?: Record<string, { score: number; grade: string }> | null
  tools: Record<ToolKey, ToolState>
}

/**
 * The verdict is BUILT, not looked up.
 *
 * `buildVerdict` (src/lib/radar/verdict.ts) assembles it deterministically from
 * the audit: score, weakest scored category, the lowest tools inside that
 * category, and the open issue counts. Re-exported here because this module was
 * the historical home of `getInterpretation`, and both ScoreHero and
 * ScoreInterpretation still read the verdict from one place.
 */
export { buildVerdict } from '@/lib/radar/verdict'
export type { Verdict } from '@/lib/radar/verdict'

function formatScanDate(timestamp: number): string {
  const d = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'today'
  if (diffDays === 1) return 'yesterday'
  if (diffDays < 7) return `${diffDays} days ago`

  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function ScoreInterpretation({
  unifiedScore,
  doneCount,
  errorCount,
  warningCount,
  previousScore,
  previousIssueCount,
  previousScanDate,
  previousToolScores,
  tools,
}: ScoreInterpretationProps) {
  if (doneCount === 0) return null

  const interp = buildVerdict({
    score: unifiedScore,
    tools,
    errorCount,
    warningCount,
  })
  const urgencyColor =
    interp.urgency === 'critical'
      ? 'var(--cta)'
      : interp.urgency === 'warning'
        ? 'var(--warning-text)'
        : interp.urgency === 'moderate'
          ? 'var(--primary)'
          : 'var(--growth)'

  // Per-tool movement against the previous saved run. A tool with no prior
  // score, or one that did not move, contributes nothing.
  //
  // Both sides are gated on TYPE, not truthiness. `!overallScore` dropped a
  // genuine score of 0 — and real audits produce them (Prompt SOV and
  // Hallucination Check both score 0) — so a drop from 90 to 0 showed no
  // movement at all. On the previous side, history written before 2026-07-26
  // stored withheld tools as `score: null`, and arithmetic against null
  // silently treats it as zero.
  const toolChanges = previousToolScores
    ? (TOOLS.map(t => {
        const currentScore = usableScore(tools[t.key])
        const prev = previousToolScores[t.key]
        if (currentScore === null || typeof prev?.score !== 'number')
          return null
        const delta = currentScore - prev.score
        if (delta === 0) return null
        return { name: t.name, delta }
      }).filter(Boolean) as { name: string; delta: number }[])
    : []

  // Declines first: a customer scanning this row needs to see what got worse
  // before what got better.
  const orderedChanges = [
    ...toolChanges.filter(c => c.delta < 0).sort((a, b) => a.delta - b.delta),
    ...toolChanges.filter(c => c.delta > 0).sort((a, b) => b.delta - a.delta),
  ]

  // Checks that crossed the failing threshold since the last run. Same
  // type gate: a null prior score is "never measured", not "was failing", so
  // it can never produce a fabricated fix.
  const resolved = previousToolScores
    ? (TOOLS.map(t => {
        const currentScore = usableScore(tools[t.key])
        const prev = previousToolScores[t.key]
        if (currentScore === null || typeof prev?.score !== 'number')
          return null
        if (prev.score < 50 && currentScore >= 50) {
          return { name: t.name, from: prev.score, to: currentScore }
        }
        return null
      }).filter(Boolean) as { name: string; from: number; to: number }[])
    : []

  const openIssues = errorCount + warningCount
  const issuesResolved =
    previousIssueCount != null
      ? Math.max(0, previousIssueCount - openIssues)
      : null
  const scoreDelta = previousScore != null ? unifiedScore - previousScore : null

  const tiles: { label: string; value: ReactNode; color?: string }[] = [
    {
      label: 'Critical issues',
      value: errorCount,
      color: errorCount > 0 ? 'var(--cta)' : undefined,
    },
  ]
  if (warningCount > 0) {
    tiles.push({
      label: 'Warnings',
      value: warningCount,
      color: 'var(--warning-text)',
    })
  }
  if (issuesResolved !== null) {
    tiles.push({
      label: 'Issues resolved',
      value: (
        <>
          {issuesResolved}
          <span className='text-base text-instrument'>
            /{previousIssueCount}
          </span>
        </>
      ),
    })
  }

  return (
    <Panel>
      {/* ── Verdict ──────────────────────────────────────────────────── */}
      <div className='flex flex-col gap-4 px-6 py-6 sm:px-7'>
        <InstrumentLabel tone='secondary'>What this means</InstrumentLabel>

        <div className='flex items-start gap-3'>
          <Zap
            size={16}
            aria-hidden='true'
            className='mt-0.5 shrink-0'
            style={{ color: urgencyColor }}
          />
          <span
            className='text-pretty text-base font-semibold leading-snug'
            style={{ color: urgencyColor }}
          >
            {interp.impact}
          </span>
        </div>

        <div
          className={`grid grid-cols-1 gap-3 ${
            tiles.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
          }`}
        >
          {tiles.map(tile => (
            <div
              key={tile.label}
              className='flex items-center justify-between gap-3 border border-hairline bg-surface-inset px-4 py-3.5'
            >
              <span className='text-sm text-muted-foreground'>
                {tile.label}
              </span>
              <span
                className='font-display text-[25px] font-semibold leading-none tabular-nums'
                style={tile.color ? { color: tile.color } : undefined}
              >
                {tile.value}
              </span>
            </div>
          ))}
        </div>

        {scoreDelta !== null && (
          <div className='flex flex-wrap items-center gap-2.5'>
            {scoreDelta > 0 ? (
              <TrendingUp
                size={14}
                aria-hidden='true'
                className='shrink-0'
                style={{ color: 'var(--growth)' }}
              />
            ) : scoreDelta < 0 ? (
              <TrendingDown
                size={14}
                aria-hidden='true'
                className='shrink-0'
                style={{ color: 'var(--cta)' }}
              />
            ) : null}
            <span
              className='font-mono text-[13px] font-semibold'
              style={{
                color:
                  scoreDelta > 0
                    ? 'var(--growth)'
                    : scoreDelta < 0
                      ? 'var(--cta)'
                      : 'var(--pm-text-muted)',
              }}
            >
              {scoreDelta > 0
                ? `+${scoreDelta}`
                : scoreDelta === 0
                  ? 'No change'
                  : scoreDelta}
              {scoreDelta !== 0 && ' points'} since last scan
            </span>
            {previousScanDate && (
              <span className='font-mono text-xs text-instrument'>
                {formatScanDate(previousScanDate)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Score changes by tool ────────────────────────────────────── */}
      {orderedChanges.length > 0 && (
        <div className='flex flex-col gap-3 border-t border-hairline px-6 py-5 sm:px-7'>
          <InstrumentLabel tone='muted'>Score changes by tool</InstrumentLabel>
          <div className='flex flex-wrap gap-2'>
            {orderedChanges.map(c => {
              const color = c.delta > 0 ? 'var(--growth)' : 'var(--cta)'
              return (
                <span
                  key={c.name}
                  className='inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-xs tabular-nums'
                  style={{
                    color,
                    borderColor: `color-mix(in srgb, ${color} 40%, transparent)`,
                  }}
                >
                  {c.name} {c.delta > 0 ? `+${c.delta}` : c.delta}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Fixes resolved ───────────────────────────────────────────── */}
      {resolved.length > 0 && (
        <div className='flex flex-col gap-3 border-t border-hairline px-6 py-5 sm:px-7'>
          <InstrumentLabel className='!text-[var(--growth)]'>
            Fixes resolved
          </InstrumentLabel>
          <div className='flex flex-col'>
            {resolved.map(r => (
              <div
                key={r.name}
                className='flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-hairline py-2.5 last:border-b-0'
              >
                <CheckCircle2
                  size={14}
                  aria-hidden='true'
                  className='shrink-0'
                  style={{ color: 'var(--growth)' }}
                />
                <span className='text-sm font-semibold'>{r.name}</span>
                <span className='font-mono text-[13px] tabular-nums text-muted-foreground'>
                  {r.from} <span className='text-instrument'>&rarr;</span>{' '}
                  <span
                    className='font-semibold'
                    style={{ color: 'var(--growth)' }}
                  >
                    {r.to}
                  </span>
                </span>
                <span className='flex-1' />
                {/* Deliberately not "from {date} run": formatScanDate yields
                    "today" / "yesterday" / "Mar 5", and no single possessive
                    phrasing is grammatical across all three. The date already
                    appears once, in the movement row above. */}
                <span className='font-mono text-[11px] text-instrument'>
                  Resolved since last scan
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Expert help — shown only when the score is genuinely bad.
            Not in the reference, kept because it is the only path from this
            panel to a human and removing it would drop a live conversion
            surface. ─────────────────────────────────────────────────── */}
      {(interp.urgency === 'critical' || interp.urgency === 'warning') && (
        <div className='flex flex-wrap items-center gap-3 border-t border-hairline px-6 py-4 sm:px-7'>
          <a
            href='https://www.pixelmojo.io/services/ai-visibility-strategy'
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-1.5 bg-[var(--cta-fill)] px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#c3086e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
          >
            Get expert help
            <ArrowRight size={12} aria-hidden='true' />
          </a>
          <span className='text-xs text-muted-foreground'>
            or fix it yourself using the action items above
          </span>
        </div>
      )}
    </Panel>
  )
}
